import { useEffect, useRef, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, MapPin, Globe, Mail } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { api, imgSrc } from "@/lib/api";
import { useI18n } from "@/i18n";

// Lightweight listing lightbox: full-size image + the same info shown on the
// main feed (title, excerpt, tags, WhatsApp/location). No long-form body.
export default function PostLightbox({ slug, posts = [], onClose, onNavigate }) {
  const { t, lang, tagLabel } = useI18n();
  const [post, setPost] = useState(undefined); // undefined=loading, null=not found
  const [slideDir, setSlideDir] = useState(0); // 1=next (enter from right), -1=prev (enter from left)
  const scrollerRef = useRef(null);
  const touchRef = useRef({ x: 0, y: 0 });

  // "Own this business? Suggest an edit" form
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [sugName, setSugName] = useState("");
  const [sugEmail, setSugEmail] = useState("");
  const [sugMsg, setSugMsg] = useState("");
  const [sugHp, setSugHp] = useState(""); // honeypot
  const [sugBusy, setSugBusy] = useState(false);
  const [sugSent, setSugSent] = useState(false);
  const [sugError, setSugError] = useState("");

  const submitSuggestion = async (e) => {
    e.preventDefault();
    if (!sugMsg.trim() || sugBusy) return;
    setSugBusy(true);
    setSugError("");
    try {
      await api.post("/suggestions", { post_slug: slug, name: sugName.trim(), email: sugEmail.trim(), message: sugMsg.trim(), website: sugHp });
      setSugSent(true);
    } catch (err) {
      setSugError(err?.response?.data?.detail || t("errGeneric"));
    } finally {
      setSugBusy(false);
    }
  };

  const index = posts.findIndex((p) => p.slug === slug);
  const prev = index > 0 ? posts[index - 1] : null;
  const next = index >= 0 && index < posts.length - 1 ? posts[index + 1] : null;

  const goPrev = useCallback(() => { if (prev && onNavigate) { setSlideDir(-1); onNavigate(prev.slug); } }, [prev, onNavigate]);
  const goNext = useCallback(() => { if (next && onNavigate) { setSlideDir(1); onNavigate(next.slug); } }, [next, onNavigate]);

  useEffect(() => {
    let active = true;
    setPost(undefined);
    setSuggestOpen(false);
    setSugSent(false);
    setSugError("");
    setSugMsg("");
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
    api.get(`/posts/${slug}?lang=${lang}`)
      .then((r) => { if (active) setPost(r.data); })
      .catch(() => { if (active) setPost(null); });
    return () => { active = false; };
  }, [slug, lang]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, goPrev, goNext]);

  const onTouchStart = (e) => {
    const t0 = e.touches[0];
    touchRef.current = { x: t0.clientX, y: t0.clientY };
  };
  const onTouchEnd = (e) => {
    const t0 = e.changedTouches[0];
    const dx = t0.clientX - touchRef.current.x;
    const dy = t0.clientY - touchRef.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  return (
    <div className="dd-lightbox" role="dialog" aria-modal="true" data-testid="post-lightbox" ref={scrollerRef}
      onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>

      <button className="dd-lightbox-close" onClick={onClose} aria-label="Close" data-testid="lightbox-close">
        <X size={24} />
      </button>

      {prev ? (
        <button className="dd-lb-arrow dd-lb-prev" aria-label="Previous post" data-testid="lightbox-prev"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}>
          <ChevronLeft size={30} />
        </button>
      ) : null}
      {next ? (
        <button className="dd-lb-arrow dd-lb-next" aria-label="Next post" data-testid="lightbox-next"
          onClick={(e) => { e.stopPropagation(); goNext(); }}>
          <ChevronRight size={30} />
        </button>
      ) : null}

      <div className="dd-lightbox-inner" onClick={(e) => e.stopPropagation()}>
        {post === undefined ? (
          <p className="dd-lightbox-loading">Loading&hellip;</p>
        ) : post === null ? (
          <p className="dd-lightbox-loading" data-testid="lightbox-not-found">Post not found.</p>
        ) : (
          <article className={`dd-single${slideDir === 1 ? " dd-slide-next" : slideDir === -1 ? " dd-slide-prev" : ""}`} key={slug}>
            {post.featured_image ? (
              <div className="dd-hero" data-testid="lightbox-image"><img src={imgSrc(post.featured_image)} alt={post.title} /></div>
            ) : null}
            <h1 className="dd-title" data-testid="lightbox-title">{post.title}</h1>
            {post.excerpt ? <p className="dd-excerpt" data-testid="lightbox-excerpt">{post.excerpt}</p> : null}
            {(post.tags && post.tags.length) ? (
              <div className="dd-feed-tags" data-testid="lightbox-tags">
                {post.tags.map((tag) => (
                  <span className="dd-tag-chip" key={tag} data-testid={`lightbox-tag-${tag}`}>{tagLabel(tag)}</span>
                ))}
              </div>
            ) : null}
            {(post.phone || post.location_url || post.website_url || post.email) ? (
              <div className="dd-contact" data-testid="lightbox-contact">
                {post.phone ? (
                  <a href={`https://wa.me/${post.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="dd-contact-link" data-testid="lightbox-phone">
                    <WhatsAppIcon size={14} />
                    {post.phone}
                  </a>
                ) : null}
                {post.location_url ? (
                  <a href={post.location_url} target="_blank" rel="noreferrer" className="dd-contact-link" data-testid="lightbox-location">
                    <MapPin size={14} strokeWidth={2} />
                    {t("viewOnMap")}
                  </a>
                ) : null}
                {post.website_url ? (
                  <a href={post.website_url} target="_blank" rel="noreferrer" className="dd-contact-link" data-testid="lightbox-website">
                    <Globe size={14} strokeWidth={2} />
                    {t("website")}
                  </a>
                ) : null}
                {post.email ? (
                  <a href={`mailto:${post.email}`} className="dd-contact-link" data-testid="lightbox-email">
                    <Mail size={14} strokeWidth={2} />
                    {post.email}
                  </a>
                ) : null}
              </div>
            ) : null}
            <div className="lb-suggest">
              {sugSent ? (
                <p className="lb-suggest-thanks" data-testid="suggest-success">{t("suggestThanks")}</p>
              ) : suggestOpen ? (
                <form className="lb-suggest-form" onSubmit={submitSuggestion} data-testid="suggest-form">
                  <div className="lb-suggest-title">{t("suggestTitle")}</div>
                  <input className="submit-input" value={sugName} onChange={(e) => setSugName(e.target.value)} placeholder={t("suggestNamePh")} maxLength={100} data-testid="suggest-name-input" />
                  <input className="submit-input" type="email" value={sugEmail} onChange={(e) => setSugEmail(e.target.value)} placeholder={t("suggestEmailPh")} data-testid="suggest-email-input" />
                  <textarea className="submit-input" rows={3} value={sugMsg} onChange={(e) => setSugMsg(e.target.value)} placeholder={t("suggestMsgPh")} maxLength={2000} data-testid="suggest-message-input" />
                  <input type="text" className="ask-hp" value={sugHp} onChange={(e) => setSugHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  {sugError ? <div className="ask-error" data-testid="suggest-error">{sugError}</div> : null}
                  <div className="lb-suggest-actions">
                    <button type="submit" className="ask-submit ask-submit-wide" disabled={sugBusy || !sugMsg.trim()} data-testid="suggest-submit">
                      {sugBusy ? "…" : t("suggestSubmit")}
                    </button>
                    <button type="button" className="ask-skip lb-suggest-cancel" onClick={() => setSuggestOpen(false)} data-testid="suggest-cancel">
                      {t("back")}
                    </button>
                  </div>
                </form>
              ) : (
                <button type="button" className="lb-suggest-link" onClick={() => setSuggestOpen(true)} data-testid="suggest-edit-link">
                  {t("suggestEdit")}
                </button>
              )}
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
